import { Component, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { select, Store } from '@ngrx/store';
import { Observable, filter, take, map, combineLatest } from 'rxjs';
import { Customer } from '../../models/Customer';
import { CustomerState } from '../../state/customer/customer.reducer';
import {
  selectCustomer,
} from '../../state/customer/customer.selector';
import {
  getCustomerByUserName,
  getCurrentCustomer,
} from '../../state/customer/customer.actions';
import { AsyncPipe, NgIf } from '@angular/common';
import { DualAuthService } from '../../services/DualAuthService';
import { UserType } from '../../models/AuthUser';
import { CoinTransaction } from '../../models/CoinTransaction';
import { getCoinTransactionsByCustomerNo } from '../../state/coin-transaction/coin-transaction.actions';
import { selectCoinTransactions } from '../../state/coin-transaction/coin-transaction.selector';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-coin-widget',
  imports: [MatCardModule, MatIconModule, MatProgressBarModule, AsyncPipe, NgIf],
  templateUrl: './coin-widget.component.html',
  styleUrl: './coin-widget.component.css',
})
export class CoinWidgetComponent implements OnInit {
  customer$: Observable<Customer | null>;
  generatedCoins$: Observable<number>;
  bonusCoins$: Observable<number>;
  creditCoins$: Observable<number>;

  constructor(private store: Store<{ customer: CustomerState }>, private auth: DualAuthService) {
    this.customer$ = store.pipe(select(selectCustomer));
    
    const coinTransactions$ = store.pipe(select(selectCoinTransactions));
    
    // Filter transactions by current year and calculate generated coins
    this.generatedCoins$ = coinTransactions$.pipe(
      map(transactions => {
        if (!transactions) return 0;
        const currentYear = new Date().getFullYear();
        return transactions
          .filter(t => {
            const date = t.createdDate ? new Date(t.createdDate) : null;
            return date && date.getFullYear() === currentYear && 
                   t.reason?.reasonText === 'Generiert (Auftragsabschluss)';
          })
          .reduce((sum, t) => sum + (t.amount || 0), 0);
      })
    );
    
    // Filter transactions by current year and calculate bonus coins
    this.bonusCoins$ = coinTransactions$.pipe(
      map(transactions => {
        if (!transactions) return 0;
        const currentYear = new Date().getFullYear();
        return transactions
          .filter(t => {
            const date = t.createdDate ? new Date(t.createdDate) : null;
            return date && date.getFullYear() === currentYear && 
                   t.reason?.reasonText === 'Generiert (Bonus)';
          })
          .reduce((sum, t) => sum + (t.amount || 0), 0);
      })
    );
    
    // Filter transactions by current year and calculate credit coins
    this.creditCoins$ = coinTransactions$.pipe(
      map(transactions => {
        if (!transactions) return 0;
        const currentYear = new Date().getFullYear();
        return transactions
          .filter(t => {
            const date = t.createdDate ? new Date(t.createdDate) : null;
            return date && date.getFullYear() === currentYear && 
                   t.reason?.reasonText === 'Gutschrift';
          })
          .reduce((sum, t) => sum + (t.amount || 0), 0);
      })
    );
  }

  ngOnInit(): void {
    const userName = localStorage.getItem('userName');
    // Only load customer data for CIAM customers; never for employees
    this.auth.currentUser$
      .pipe(filter(u => !!u), take(1))
      .subscribe(() => {
        if (!this.auth.isCustomer()) return;
        if (userName && userName.trim().length > 0) {
          this.store.dispatch(getCustomerByUserName({ userName }));
        } else {
          this.store.dispatch(getCurrentCustomer());
        }
      });
    
    // Dispatch action to get coin transactions based on customer
    this.customer$
      .pipe(
        filter(c => !!c && !!c.reflexCustomerNo),
        take(1)
      )
      .subscribe(customer => {
        if (customer?.reflexCustomerNo) {
          this.store.dispatch(getCoinTransactionsByCustomerNo({ customerNo: customer.reflexCustomerNo }));
        }
      });
  }
}
